import type { Exercise } from '../../types.ts';
export const batch6Math: Exercise[] = [
  {
    id: 'math-random-cut',
    category: 'math',
    title: 'A cut ribbon',
    topic: 'A cut ribbon',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-random-cut',
        prompt:
          'Choose a cut point uniformly along a ribbon of length 1 meter. Keep the longer of the two pieces. What is its expected length?',
        answer: {
          kind: 'number',
          value: 0.75,
          unit: 'meters',
          tolerance: 0.0001,
        },
        explanation:
          'Uniform cut positions produce a uniform longer-piece length between 0.5 and 1 meter. Its mean is the midpoint, 0.75 meters. Averaging cut positions first would instead lose the information needed by the longer-piece rule.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'A cut ribbon — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A uniform cut means equal-length intervals of possible cut locations have equal probability. Let X be the distance from the left end, between zero and one. The two piece lengths are X and 1−X. The retained length is the larger of those values, not their sum and not the mean of the two pieces.',
            },
            {
              kind: 'paragraph',
              text: 'Let L denote the length we keep. For a proposed threshold l, the expression P(L ≤ l) means the probability that the retained piece is no longer than l. To find it, identify which cut positions satisfy that condition. Their combined interval length gives the probability because the original cut is uniform on a one-meter interval.',
            },
            {
              kind: 'paragraph',
              text: 'Use symmetry around the midpoint. For cuts in the left half, the retained length is 1−X; for cuts in the right half, it is X. As a cut moves uniformly across either half, the longer piece ranges uniformly from one half to one. Each retained length inside that interval has two symmetric possible cut locations with the same local probability weight.',
            },
            {
              kind: 'diagram',
              drawing: 'ribbon-cut',
              caption: 'Two different cut locations can produce the same longer piece.',
              description:
                'A one-meter ribbon cut at 0.3 meters has pieces 0.3 and 0.7 meters. Its mirror cut at 0.7 meters has pieces 0.7 and 0.3 meters. The retained longer piece is 0.7 meters in either case.',
            },
            {
              kind: 'example',
              title: 'A discrete approximation',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Allow cuts only at 0.1,0.3,0.5,0.7 and 0.9, all equally likely. The longer pieces are 0.9,0.7,0.5,0.7 and 0.9 meters. Their average is 0.74 meters. This is a different, discrete experiment, but it illustrates why lengths near three quarters arise rather than one half.',
                },
                {
                  kind: 'table',
                  caption: 'Map cuts to retained length',
                  headers: ['Cut location', 'Longer length'],
                  rows: [
                    ['0.1', '0.9'],
                    ['0.3', '0.7'],
                    ['0.5', '0.5'],
                    ['0.7', '0.7'],
                    ['0.9', '0.9'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For a continuous uniform quantity on an interval, the mean is its midpoint. One way to derive this is to pair points equally far from the center; every pair has that center as its average. Another is to integrate length times probability density. Both depend on equal probability per unit length, not merely on knowing the endpoints.',
            },
            {
              kind: 'equation',
              label: 'Longer-piece distribution',
              lines: [
                {
                  left: 'P(L≤l)',
                  right: '2l−1 for 1/2≤l≤1',
                  reason: 'Both pieces are at most l exactly when the cut lies from 1−l to l.',
                },
              ],
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The cumulative probability 2l−1 grows at a constant rate over the interval from one half to one. Thus L is uniform on that interval, and its mean is (1/2+1)/2=3/4 meter.',
            },
            {
              kind: 'equation',
              label: 'Expected retained length',
              lines: [
                {
                  left: 'E(L)',
                  right: '0.75 m',
                  reason:
                    'Pair equally likely lengths around the midpoint of the retained-length interval.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'An independent area calculation integrates max(x,1−x) over cut positions 0≤x≤1. The two equal halves give 2 times the integral of 1−x from 0 to 1/2, or 2(1/2−1/8)=3/4. Geometrically these are two trapezoids under a V-shaped length graph.',
            },
            {
              kind: 'paragraph',
              text: 'The cut point’s expected position is one half, but evaluating the longer-piece rule only at that average cut would give one half and be wrong. Averaging before applying a nonlinear selection rule need not equal averaging the selected lengths. The complement provides a check: the expected shorter length is one quarter, and the two expected lengths sum to the fixed one-meter total.',
            },
            {
              kind: 'paragraph',
              text: 'Uniform cut positions produce a uniform longer-piece length between 0.5 and 1 meter. Its mean is the midpoint, 0.75 meters. Averaging cut positions first would instead lose the information needed by the longer-piece rule.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-line-fit',
    category: 'math',
    title: 'Calibration readings',
    topic: 'Calibration readings',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-line-fit',
        prompt:
          'Fit a line y=a+bx by least squares to the three points (0,1), (1,2), (2,5), minimizing the sum of squared vertical residuals. What value does the fitted line predict at x=3?',
        answer: {
          kind: 'number',
          value: 6.666666666666667,
          unit: 'predicted y',
          tolerance: 0.0001,
        },
        explanation:
          'The prediction is 20/3. Because x=3 lies outside the observed range 0–2, this is extrapolation under the fitted linear model, not a guarantee about a real instrument’s behavior there.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'Calibration readings — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A fitted line summarizes a relationship without necessarily passing through each observation. A vertical residual is observed y minus predicted y at the same x. Least squares chooses the line minimizing the sum of residuals squared. Squaring prevents positive and negative errors from canceling and penalizes larger misses more strongly.',
            },
            {
              kind: 'paragraph',
              text: 'Write the line as predicted y = a + bx, where a is the intercept and b is the slope. A bar denotes the arithmetic mean: x̄ is the sum of observed x values divided by their number. For a fixed slope, moving the line up or down changes every residual by the same amount. The squared-error sum is smallest when their mean is zero; otherwise shifting the line toward that mean reduces the total.',
            },
            {
              kind: 'paragraph',
              text: 'Centering the residuals gives a = ȳ − bx̄. Now write each centered x as u and each centered y as v. The residual becomes v − bu. Expanding its square and summing gives Σv² − 2bΣuv + b²Σu². This is a quadratic bowl in b. Completing the square places its minimum at b = Σuv/Σu², provided Σu² is positive. The formula therefore follows from minimizing the chosen errors, rather than being an unrelated rule to memorize.',
            },
            {
              kind: 'equation',
              label: 'Least-squares slope',
              lines: [
                {
                  left: 'b',
                  right: {
                    numerator: 'Σ[(x − x̄)(y − ȳ)]',
                    denominator: 'Σ[(x − x̄)²]',
                  },
                  reason:
                    'The denominator must be positive, so the x values cannot all be identical.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The numerator measures whether above-average x tends to accompany above-average y. The denominator scales that co-movement by the spread in x. Dividing gives y-units per x-unit. These formulas arise from the stated vertical squared-error objective; a different error definition could give another line.',
            },
            {
              kind: 'example',
              title: 'A distinct three-point fit',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For (0,0),(1,2),(2,1), the means are x̄=1 and ȳ=1. Centered products are 1,0,0, summing to 1; centered x squares are 1,0,1, summing to 2. Thus b=1/2 and a=1/2. At x=1 the prediction is 1, the mean y, even though the observed middle y is 2.',
                },
              ],
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'For the daily points, x̄=1 and ȳ=8/3. The centered x values are −1,0,1; centered y values are −5/3,−2/3,7/3.',
            },
            {
              kind: 'table',
              caption: 'Build the slope from centered observations',
              headers: ['Point', 'Centered product', 'Centered x square'],
              rows: [
                ['(0,1)', '5/3', '1'],
                ['(1,2)', '0', '0'],
                ['(2,5)', '7/3', '1'],
                ['Total', '4', '2'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The slope is 4/2=2 and intercept is 8/3−2=2/3. At x=3 the prediction is 2/3+6=20/3. The residuals at the observed x values are 1/3,−2/3,1/3; they sum to zero. Their x-weighted sum is also zero, confirming the normal equations for the least-squares minimum.',
            },
            {
              kind: 'paragraph',
              text: 'Connecting just the first and last points gives slope 2 but intercept 1, overlooking the middle observation. That line has larger total squared error. The fitted line is a summary of all three points, not necessarily an interpolation through the endpoints.',
            },
            {
              kind: 'paragraph',
              text: 'The prediction is 20/3. Because x=3 lies outside the observed range 0–2, this is extrapolation under the fitted linear model, not a guarantee about a real instrument’s behavior there.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-sound-level',
    category: 'math',
    title: 'Combining sources',
    topic: 'Combining sources',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-sound-level',
        prompt:
          'In a model, independent sound sources contribute intensities that add. Two identical sources each produce level 60 dB at a receiver. Level is defined as L=10 log₁₀(I/I₀), using the same reference intensity I₀. What is their combined level? Round to the nearest 0.01 dB.',
        answer: {
          kind: 'number',
          value: 63.01029995663981,
          unit: 'dB',
          tolerance: 0.0051,
        },
        explanation:
          'The combined modeled level is 63.01 dB. This is a mathematical intensity calculation, not a statement about perceived loudness or safe exposure duration.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'Combining sources — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A logarithmic level compresses a wide range of intensities. The expression log₁₀(z) asks which power of ten equals z. For example, log₁₀(1000)=3 because 10³=1000. A decibel level compares intensity I with a reference I₀, making the ratio dimensionless before taking its logarithm.',
            },
            {
              kind: 'paragraph',
              text: 'Levels are not intensities and generally cannot be added. First translate each level back to its intensity ratio, add the physical intensities under the given model, and convert the resulting ratio back to a level. The model explicitly supplies intensity addition; coherent wave interference could require more information about phase and position.',
            },
            {
              kind: 'equation',
              label: 'Undo the logarithm',
              lines: [
                {
                  left: 'I/I₀',
                  right: '10^(L/10)',
                  reason: 'Divide the level by ten, then use that number as a base-ten exponent.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Two different smaller levels',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A 20 dB source has intensity 100 I₀; a 30 dB source has 1000 I₀. Together they give 1100 I₀, so the level is 10 log₁₀(1100), approximately 30.41 dB. Adding 20+30 to get 50 dB would correspond to 100,000 I₀, far above the actual sum.',
                },
                {
                  kind: 'table',
                  caption: 'Convert before adding',
                  headers: ['Level', 'Intensity ratio'],
                  rows: [
                    ['20 dB', '100'],
                    ['30 dB', '1000'],
                    ['Combined', '1100'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For n equal sources, total intensity is n times one source’s intensity. The logarithm of a product is the sum of logarithms, so the level increase is 10 log₁₀(n). It depends on the multiplication factor, not on the original level. Ten equal sources increase the level by 10 dB, while doubling produces a smaller increase.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Each 60 dB source has intensity ratio 10⁶. Two give 2×10⁶. The combined level is 10(log₁₀2+6).',
            },
            {
              kind: 'equation',
              label: 'Combined level',
              lines: [
                {
                  left: '60 + 10 log₁₀2',
                  right: '63.010299… dB',
                  reason: 'Round only the final level to the requested precision.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The result is 63.01 dB. A direct inverse check gives 10^(63.010299…/10)=2,000,000, twice the individual ratio. Reporting 120 dB would imply an intensity ratio 10¹², a million times one source rather than twice one.',
            },
            {
              kind: 'paragraph',
              text: 'Another common mistake adds 3 to the intensity rather than to the decibel level. The approximate three-decibel rule describes a multiplicative physical change through an additive logarithmic scale. Keep the quantities and their units distinct at every step.',
            },
            {
              kind: 'paragraph',
              text: 'The combined modeled level is 63.01 dB. This is a mathematical intensity calculation, not a statement about perceived loudness or safe exposure duration.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-marked-fish',
    category: 'math',
    title: 'A marked sample',
    topic: 'A marked sample',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-marked-fish',
        prompt:
          'In a closed population, 40 fish are caught, marked and released. Later, a simple random sample of 50 fish contains 10 marked fish. Assuming marks persist, marking does not affect capture, and fish have mixed thoroughly, estimate the population by equating the sample’s marked fraction with the population’s marked fraction.',
        answer: {
          kind: 'number',
          value: 200,
          unit: 'fish',
          tolerance: 0.0001,
        },
        explanation:
          'The requested estimate is 200 fish. The result depends on representative recapture and persistent marks, not merely on substituting three counts into a formula.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'A marked sample — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Marking creates a known subgroup inside an unknown total population. A later representative sample provides an estimate of that subgroup’s fraction. If M fish were marked and the total population is N, the marked fraction is M/N. If a later sample of size n includes m marked fish, its observed marked fraction is m/n.',
            },
            {
              kind: 'paragraph',
              text: 'Equating these fractions gives an estimator, not an exact identity that every random sample must satisfy. Random samples fluctuate. The estimator uses the observed sample proportion as a stand-in for the unknown population proportion, then solves for the total. Its reliability depends on the sampling and biological assumptions.',
            },
            {
              kind: 'equation',
              label: 'Match the proportions',
              lines: [
                {
                  left: 'Estimated population N̂',
                  right: {
                    numerator: 'M × n',
                    denominator: 'm',
                  },
                  reason:
                    'Solving M/N = m/n for N gives this proportion-matching estimator; it requires m > 0.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A jar model',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Place 20 marked beads among an unknown number of otherwise identical beads and mix. If a random sample of 30 contains 5 marked beads, the sample marked fraction is 1/6. Treating 20 as one sixth of the total gives an estimate of 120 beads.',
                },
                {
                  kind: 'table',
                  caption: 'Known group and observed fraction',
                  headers: ['Quantity', 'Value'],
                  rows: [
                    ['Marked in population', '20'],
                    ['Marked fraction in sample', '5/30'],
                    ['Estimated total', '20 ÷ (5/30) =120'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'If marked individuals are easier to recapture, the sample overrepresents them. The observed marked fraction becomes too high, leading this formula to underestimate total population. Lost marks or avoidance of recapture can push in the opposite direction. A closed-population assumption also excludes births, deaths and migration between the two samples.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The observed marked fraction is 10/50=1/5. Equate 40/N with 1/5 to obtain N=200. Equivalently, the known 40 marked fish are estimated to be twenty percent of all fish.',
            },
            {
              kind: 'equation',
              label: 'Daily estimate',
              lines: [
                {
                  left: '40×50/10',
                  right: '200 fish',
                  reason:
                    'Scale the known marked subgroup by the reciprocal of its estimated fraction.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'This estimate passes basic consistency checks: it is at least the 40 marked fish and at least the 50 fish in the second sample. The two captures contain 80 distinct fish, because ten appear in both; the estimate is also above that directly observed minimum.',
            },
            {
              kind: 'paragraph',
              text: 'The population is not proved to contain exactly 200 fish. A population of a different size could produce the same sample by chance. More data or an uncertainty analysis would be needed to express precision. The question explicitly asks for the proportion-matching estimate so it does not pretend a single capture result uniquely determines an unknown biological total.',
            },
            {
              kind: 'paragraph',
              text: 'The requested estimate is 200 fish. The result depends on representative recapture and persistent marks, not merely on substituting three counts into a formula.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'math-walk-boundaries',
    category: 'math',
    title: 'A short random walk',
    topic: 'A short random walk',
    difficulty: 'Challenging',
    questions: [
      {
        id: 'b6-walk-boundaries',
        prompt:
          'A token starts at position 2 on the integer line 0,1,2,3,4,5. At each step, independently, it moves one position left or right with equal probability. It stops on reaching 0 or 5. What is the probability it stops at 5?',
        answer: {
          kind: 'number',
          value: 0.4,
          unit: 'probability',
          tolerance: 0.0001,
        },
        explanation:
          'The probability is 0.4. If step probabilities were biased, the averaging equation would use unequal weights and the solution would generally not be linear.',
        sectionIds: ['concept', 'application'],
        evidence: 'Independently checked in tests/batch6.test.ts.',
      },
    ],
    reading: {
      title: 'A short random walk — concepts and method',
      authorship: 'Original teaching and questions prepared with AI assistance.',
      sources: [],
      sections: [
        {
          id: 'concept',
          title: 'Build the method',
          role: 'Concept',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A stopping probability can be solved by conditioning on the first step. Let pᵢ be the chance of reaching the right boundary before the left when starting at i. At an interior position, the next step goes left or right equally often. After that step, the same problem begins at the neighboring position.',
            },
            {
              kind: 'equation',
              label: 'First-step equation',
              lines: [
                {
                  left: 'pᵢ',
                  right: '(pᵢ₋₁ + pᵢ₊₁)/2',
                  reason: 'Condition on the two equally likely next states.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For a general interval whose left endpoint is 0 and right endpoint is n, n is the total number of unit steps across the interval. The boundary values are p₀=0 and pₙ=1: starting at the left endpoint has already lost the event, and starting at the right endpoint has already achieved it. Rearrange the interior equation as pᵢ₊₁−pᵢ=pᵢ−pᵢ₋₁. Neighboring probability increments are equal, so the solution rises linearly between the boundary values.',
            },
            {
              kind: 'example',
              title: 'Three positions, 0 through 2',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The sole interior position is 1. One step immediately reaches either endpoint, so p₁=1/2. For positions 0 through 4, the linear sequence is 0,1/4,1/2,3/4,1. Check position 1: the average of 0 and 1/2 is 1/4, exactly its listed value.',
                },
                {
                  kind: 'table',
                  caption: 'A distinct four-interval walk',
                  headers: ['Starting position', 'Right-boundary chance'],
                  rows: [
                    ['0', '0'],
                    ['1', '1/4'],
                    ['2', '1/2'],
                    ['3', '3/4'],
                    ['4', '1'],
                  ],
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The walk stops with probability one on a finite interval. From any interior position, a fixed run of n steps in one direction would reach a boundary; such a run has positive probability. Over repeated blocks, the probability of avoiding absorption forever shrinks to zero. This rules out a missing probability assigned to wandering indefinitely.',
            },
          ],
        },
        {
          id: 'application',
          title: 'Apply and check',
          role: 'Daily application',
          blocks: [
            {
              kind: 'paragraph',
              text: 'With endpoints 0 and 5, five equal probability increments must sum to one. Each increment is 1/5. Starting at 2 therefore gives p₂=2/5.',
            },
            {
              kind: 'equation',
              label: 'Requested probability',
              lines: [
                {
                  left: 'p₂',
                  right: '2/5 =0.4',
                  reason:
                    'The starting point lies two of the five equal intervals from the left endpoint.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Check the neighboring equations: p₁=1/5, p₂=2/5 and p₃=3/5 satisfy p₂=(p₁+p₃)/2. Continuing to endpoints gives the unique linear solution. Solving the four simultaneous first-step equations directly yields the same values.',
            },
            {
              kind: 'paragraph',
              text: 'Equal probability of each individual step does not imply equal probability of the two final endpoints from every starting point. Position 2 is closer to 0 than to 5, so a right-end probability below one half is sensible. Only the geometric midpoint would make the boundary events symmetric.',
            },
            {
              kind: 'paragraph',
              text: 'The probability is 0.4. If step probabilities were biased, the averaging equation would use unequal weights and the solution would generally not be linear.',
            },
          ],
        },
      ],
    },
  },
];
